/* ==========================================================================
   VITA100 promo — apply and confirm
   Added 2026-08-19. Revised 2026-08-19 (copy removed, click-gated state).

   Behaviour:
   - 一鍵套用 is the only interactive control. Clicking it applies the code in
     the background (no page navigation) and shows a confirmation toast.
   - The green 已套用 state appears ONLY after the customer has actually
     clicked. It is never inferred from a discount that happens to be sitting
     in the session from some earlier visit.
   - Once clicked, the state persists across pages for that browsing session,
     and is re-verified against /cart.js so it is never shown when the code is
     not genuinely on the cart.
   - If JS or the fetch fails, the control falls back to its plain href, which
     is the original /discount/CODE navigation. Nothing depends on JS.
   ========================================================================== */

(function () {
  'use strict';

  var TOAST_MS = 5000;
  var STICKY_KEY = 'vita-promo-dismissed';
  var CLICKED_KEY = 'vita-promo-clicked';

  var root = document.documentElement;
  var toastTimer = null;

  /* ------------------------------------------------------------ utilities */

  function codeFromControl(el) {
    return (el.getAttribute('data-vita-code') || '').trim();
  }

  function readFlag(key) {
    try {
      return sessionStorage.getItem(key) === '1';
    } catch (error) {
      return false;
    }
  }

  function writeFlag(key) {
    try {
      sessionStorage.setItem(key, '1');
    } catch (error) {
      /* private mode — the state simply does not carry to the next page */
    }
  }

  function cartHasCode(code) {
    var url = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root)
      ? window.Shopify.routes.root + 'cart.js'
      : '/cart.js';
    return fetch(url, { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var codes = (cart && cart.discount_codes) || [];
        return codes.some(function (d) {
          return d.code && d.code.toUpperCase() === code.toUpperCase();
        });
      })
      .catch(function () { return false; });
  }

  /* ---------------------------------------------------------------- toast */

  function ensureToast() {
    var toast = document.querySelector('[data-vita-toast]');
    if (toast) return toast;

    toast = document.createElement('div');
    toast.className = 'vita-toast';
    toast.setAttribute('data-vita-toast', '');
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');
    toast.hidden = true;
    toast.innerHTML =
      '<svg class="vita-toast__tick" viewBox="0 0 20 20" fill="none" aria-hidden="true" focusable="false">' +
        '<circle cx="10" cy="10" r="9" fill="currentColor" opacity="0.15"/>' +
        '<path d="M6 10.4l2.6 2.6L14.2 7.4" stroke="currentColor" stroke-width="1.8" ' +
        'stroke-linecap="round" stroke-linejoin="round"/>' +
      '</svg>' +
      '<span data-vita-toast-text></span>' +
      '<button type="button" class="vita-toast__close" data-vita-toast-close aria-label="關閉提示">&times;</button>';

    document.body.appendChild(toast);

    toast.querySelector('[data-vita-toast-close]').addEventListener('click', function () {
      hideToast(toast);
    });

    return toast;
  }

  function hideToast(toast) {
    toast.classList.remove('is-visible');
    window.clearTimeout(toastTimer);
    toastTimer = window.setTimeout(function () { toast.hidden = true; }, 250);
  }

  function showToast(message) {
    var toast = ensureToast();
    toast.querySelector('[data-vita-toast-text]').textContent = message;
    toast.hidden = false;

    void toast.offsetWidth;
    toast.classList.add('is-visible');

    window.clearTimeout(toastTimer);
    toastTimer = window.setTimeout(function () { hideToast(toast); }, TOAST_MS);
  }

  /* -------------------------------------------------------- applied state */

  function markApplied() {
    root.classList.add('vita-is-applied');

    document.querySelectorAll('[data-vita-apply]').forEach(function (el) {
      el.classList.add('is-applied');
      el.setAttribute('aria-disabled', 'true');
      var label = el.querySelector('[data-vita-apply-label]');
      if (label) label.textContent = el.getAttribute('data-vita-applied-label') || '已套用';
    });
  }

  function setBusy(el, busy) {
    el.classList.toggle('is-busy', busy);
    var label = el.querySelector('[data-vita-apply-label]');
    if (!label) return;
    if (busy) {
      label.setAttribute('data-vita-prev-label', label.textContent);
      label.textContent = '套用中…';
    } else {
      var prev = label.getAttribute('data-vita-prev-label');
      if (prev) label.textContent = prev;
    }
  }

  /* ---------------------------------------------------------------- apply */

  function applyCode(control) {
    var code = codeFromControl(control);
    if (!code) return;

    setBusy(control, true);

    fetch(control.getAttribute('href'), { credentials: 'same-origin' })
      .then(function () { return cartHasCode(code); })
      .then(function (ok) {
        setBusy(control, false);
        if (ok) {
          writeFlag(CLICKED_KEY);
          markApplied();
          showToast('已套用折扣碼 ' + code + '，結帳時自動折抵 NT$100。');
        } else {
          window.location.href = control.getAttribute('href');
        }
      })
      .catch(function () {
        setBusy(control, false);
        window.location.href = control.getAttribute('href');
      });
  }

  /* -------------------------------------------------------------- wiring */

  document.addEventListener('click', function (event) {
    var apply = event.target.closest('[data-vita-apply]');
    if (!apply) return;

    event.preventDefault();

    if (apply.classList.contains('is-busy')) return;

    if (apply.classList.contains('is-applied')) {
      showToast('折扣碼 ' + codeFromControl(apply) + ' 已套用，結帳時自動折抵。');
      return;
    }

    applyCode(apply);
  });

  /* --------------------------------------------------------- sticky bar */

  function initSticky() {
    var sticky = document.querySelector('[data-vita-sticky]');
    if (!sticky) return;

    if (!readFlag(STICKY_KEY)) {
      sticky.hidden = false;
      document.body.classList.add('vita-sticky-on');
    }

    var close = sticky.querySelector('[data-vita-dismiss]');
    if (!close) return;
    close.addEventListener('click', function () {
      sticky.hidden = true;
      document.body.classList.remove('vita-sticky-on');
      writeFlag(STICKY_KEY);
    });
  }

  /* ------------------------------------------------------------ on load */

  function init() {
    initSticky();

    var anyControl = document.querySelector('[data-vita-apply]');
    if (!anyControl) return;

    // Only restore the applied state if THIS customer clicked 一鍵套用 during
    // this browsing session. A code left over in the session from an earlier
    // visit does not turn the button green on its own.
    if (!readFlag(CLICKED_KEY)) return;

    // They did click — confirm the code is still genuinely on the cart before
    // showing 已套用, so the state can never outlive the discount itself.
    cartHasCode(codeFromControl(anyControl)).then(function (ok) {
      if (ok) markApplied();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
